import LongText from 'components/atoms/long_text';
import ManagementButton from 'components/atoms/management_button/management_button';
import moment from 'moment';
import React from 'react';
import { ManagedData } from 'store/modules/management_store/types';
import iconUser from 'assets/images/user-icon.svg';
import SelectButton from 'components/atoms/select_button';

interface DataTableItem {
  id: number;
  edict: string;
  object: string;
  public_body: string;
  city: string;
  state: string;
  bidding: ManagedData;
  isManaged: boolean;
  isChecked: boolean;
  dataValidadeTimestamp: string;
  task_amount: number;
}
const EDITAL_GROW = 0;
const ORGAO_GROW = 2;
const OBJETO_GROW = 3;
const PRAZO_GROW = 1;
const SELECT_GROW = 0;
const TAREFAS_GROW = 0;
const BUTTON_GROW = 0;
const GERENCIADORES_GROW = 0;

const STATUS_LABELS: Record<number, string> = {
  9: 'SUSPENSA SINE DIE',
  10: 'ADIADA SINE DIE',
  11: 'CANCELADA',
  12: 'SUSPENSO',
  13: 'REVOGADA',
  14: 'ANULADA',
};

export const datatableData = (
  managedList: ManagedData[],
  selectedManageds: Array<number>,
): DataTableItem[] => {
  return managedList?.map((bidding) => {
    const isChecked = selectedManageds.includes(bidding.id);
    return {
      id: bidding.id,
      edict: bidding.edital,
      object: bidding.objeto,
      public_body: bidding?.public_body?.nome,
      city: bidding.orgao_cidade,
      state: bidding.orgao_estado,
      bidding,
      isManaged: bidding.managed,
      dataValidadeTimestamp: moment(bidding.data_validade).format('X'),
      task_amount: bidding.total_managed_opened_tasks || 0,
      managers: bidding.managers,
      isChecked,
    };
  });
};

export const datatableColumns = (
  setModalManagementTaskOpened: React.Dispatch<React.SetStateAction<boolean>>,
  setModalFinishBiddingOpened: React.Dispatch<React.SetStateAction<boolean>>,
  setModalRemoveBiddingOpened: React.Dispatch<React.SetStateAction<boolean>>,
  setTaskType: React.Dispatch<React.SetStateAction<string>>,
  setCurrentBiddingId: React.Dispatch<React.SetStateAction<number>>,
  setCurrentBiddingTotalOpenedTasks: React.Dispatch<React.SetStateAction<number>>,
  toggleSelected: (id: number) => void,
) => {
  const formatNames = (names: string[]) => {
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} e ${names[1]}`;

    return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  };
  return [
    {
      name: '',
      width: '80px',
      sortable: true,
      selector: (data: DataTableItem) => data.id,
      center: true,
      grow: SELECT_GROW,
      cell: (data: DataTableItem) => (
        <div
          className="databalloon"
          aria-label={data.isChecked ? 'Remover da seleção' : 'Selecionar licitação'}
          data-balloon-pos="right"
        >
          <SelectButton
            id={data.id}
            isChecked={data.isChecked}
            toggleSelected={toggleSelected}
            text=""
          />
        </div>
      ),
    },
    {
      name: 'Edital',
      width: '110px',
      sortable: true,
      selector: (data: DataTableItem) => data.edict,
      center: true,
      grow: EDITAL_GROW,
      cell: (data: DataTableItem) => (
        <div className="text-center">
          <p className="m-0 p-1">{data.edict}</p>
        </div>
      ),
    },
    {
      name: 'Órgão',
      sortable: true,
      selector: (data: DataTableItem) => data.public_body,
      wrap: true,
      grow: ORGAO_GROW,
      cell: (data: DataTableItem) => (
        <div>
          <div className="m-0 font-weight-bold">{data.public_body}</div>
          <div>
            {data.city}/{data.state}
          </div>
        </div>
      ),
    },
    {
      name: 'Objeto',
      id: 'objeto',
      sortable: true,
      selector: (data: DataTableItem) => data.object,
      wrap: true,
      ignoreRowClick: true,
      grow: OBJETO_GROW,
      cell: (data: DataTableItem) => <LongText fullText={data.object} />,
    },
    {
      name: 'Prazo',
      sortable: true,
      wrap: true,
      grow: PRAZO_GROW,
      selector: (data: DataTableItem) => data.dataValidadeTimestamp,
      cell: (data: DataTableItem) => (
        <div>
          {data.bidding.datahora_visita ? (
            <div>
              <p className="m-0">
                <b>Visita:</b> {moment(data.bidding.datahora_visita).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
          ) : (
            ''
          )}
          {data.bidding.datahora_documento ? (
            <div>
              <p className="m-0">
                <b>Documento:</b>{' '}
                {moment(data.bidding.datahora_documento).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
          ) : (
            ''
          )}
          {data.bidding.datahora_abertura ? (
            <div>
              <p className="m-0">
                <b>Abertura:</b> {moment(data.bidding.datahora_abertura).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
          ) : (
            ''
          )}
          {data.bidding.datahora_prazo ? (
            <div>
              <p className="m-0">
                <b>Prazo:</b> {moment(data.bidding.datahora_prazo).format('DD/MM/YYYY HH:mm')}
              </p>
            </div>
          ) : (
            ''
          )}
          {STATUS_LABELS[data.bidding.tipo_agrupamento_id] && (
            <div>
              <p className="m-0">
                <b>{STATUS_LABELS[data.bidding.tipo_agrupamento_id]}</b>
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'Gerenciado por',
      width: '120px',
      center: true,
      sortable: true,
      grow: GERENCIADORES_GROW,
      cell: (data: DataTableItem) => {
        const namesArray = data.bidding.managers?.map((m) => m.name) || [];
        const names = formatNames(namesArray) || 'Nenhum membro do time gerenciando essa licitação';

        return (
          <div
            className="databalloon badge badge-secondary managers d-flex"
            data-balloon-length="medium"
            aria-label={`Gerenciado por: ${names}`}
            style={{ fontSize: '0.85rem', padding: '0.30rem' }}
          >
            <img src={iconUser} alt="icone de usuário" />
            <span>{data.bidding.managers?.length || 0}</span>
          </div>
        );
      },
    },
    {
      name: 'Tarefas em aberto',
      width: '120px',
      center: true,
      sortable: true,
      selector: (data: DataTableItem) => data.task_amount,
      grow: TAREFAS_GROW,
      cell: (data: DataTableItem) => (
        <span className="badge badge-secondary" style={{ fontSize: '0.85rem', padding: '0.30rem' }}>
          {data.task_amount}
        </span>
      ),
    },
    {
      name: '',
      width: '180px',
      center: true,
      allowOverflow: true,
      grow: BUTTON_GROW,
      cell: (data: DataTableItem) => (
        <ManagementButton
          biddingId={data.id}
          isManaged={data.isManaged}
          totalOpenedTasks={data.task_amount}
          setModalManagementTaskOpened={setModalManagementTaskOpened}
          setModalFinishBiddingOpened={setModalFinishBiddingOpened}
          setModalRemoveBiddingOpened={setModalRemoveBiddingOpened}
          setTaskType={setTaskType}
          setCurrentBiddingId={setCurrentBiddingId}
          setCurrentBiddingTotalOpenedTasks={setCurrentBiddingTotalOpenedTasks}
          source="management"
        />
      ),
    },
  ];
};
